import { createContext, useContext, useLayoutEffect, useRef, useState, type ComponentProps } from 'react';
import { CopilotChat, CopilotChatInput, type CopilotChatInputProps } from '@copilotkit/react-core/v2';
import { uploadBrandMedia } from '../lib/upload';
import { useSession } from '../lib/session';

const AttachmentState = createContext({ ready: false, uploading: false });

/** Keep CopilotKit's message, attachment, scroll and run owners; replace only its input slot. */
export function PhotoChatView(props: ComponentProps<typeof CopilotChat.View>) {
  const state = { ready: !!props.attachments?.some(a => a.status === 'ready'), uploading: !!props.attachments?.some(a => a.status === 'uploading') };
  return <AttachmentState.Provider value={state}><CopilotChat.View {...props} input={ComposerSlot} suggestions={[]} /></AttachmentState.Provider>;
}

function ChatComposer({ value, onChange, onSubmitMessage, isRunning, onStop, onAddFile }: CopilotChatInputProps) {
  const [local, setLocal] = useState('');
  const text = value ?? local;
  const area = useRef<HTMLTextAreaElement>(null);
  const { ready, uploading } = useContext(AttachmentState);
  const change = (next: string) => { setLocal(next); onChange?.(next); };
  useLayoutEffect(() => { if (area.current) { area.current.style.height = '0px'; area.current.style.height = `${Math.min(144, area.current.scrollHeight)}px`; } }, [text]);
  const submit = () => {
    if (isRunning || uploading || (!text.trim() && !ready)) return;
    onSubmitMessage?.(text.trim());
    change('');
  };
  return <div className="ch-composer" data-testid="copilot-chat-input">
    <button type="button" className="ch-icon" aria-label="Attach photos" title="Attach photos" onClick={onAddFile} disabled={uploading || isRunning}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m21 11-8.5 8.5a6 6 0 0 1-8.5-8.5l9-9a4 4 0 0 1 5.7 5.7l-9 9a2 2 0 0 1-2.8-2.8l8.4-8.4" /></svg>
    </button>
    <textarea ref={area} rows={1} aria-label="Message" placeholder="Message…" data-testid="copilot-chat-textarea" value={text}
      onChange={e => change(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) { e.preventDefault(); submit(); } }} />
    <button type="button" className="ch-icon ch-send" data-testid="copilot-send-button" aria-label={isRunning ? 'Stop' : 'Send'} title={isRunning ? 'Stop' : 'Send'}
      disabled={!isRunning && (uploading || (!text.trim() && !ready))} onClick={isRunning ? onStop : submit}>
      {isRunning ? <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2" /></svg> : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V5m-6 6 6-6 6 6" /></svg>}
    </button>
  </div>;
}

const ComposerSlot = Object.assign(ChatComposer, CopilotChatInput);

/** Chat gets a small visual copy, not a private storage URL that the provider cannot fetch. Originals retain existing draft consent. */
export async function photoForChat(file: File, brandId: string) {
  if (!['image/jpeg', 'image/png'].includes(file.type)) throw new Error('Choose a JPG or PNG photo.');
  if (file.size > 20 * 1024 * 1024) throw new Error('Choose a photo smaller than 20 MB.');
  const image = await createImageBitmap(file);
  let data: string;
  try {
    const scale = Math.min(1, 1024 / Math.max(image.width, image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.width * scale)); canvas.height = Math.max(1, Math.round(image.height * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not prepare this photo.');
    context.fillStyle = '#fff'; context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    data = canvas.toDataURL('image/jpeg', 0.75).split(',')[1]!;
    if (data.length > 600_000) throw new Error('This photo is too detailed. Choose a smaller image.');
  } finally { image.close(); }
  await uploadBrandMedia(brandId, file, () => {});
  return { type: 'data' as const, value: data, mimeType: 'image/jpeg', metadata: { filename: file.name } };
}

export function PhotoChat({ threadId, labels }: { threadId: string; labels: ComponentProps<typeof CopilotChat>['labels'] }) {
  const { brand } = useSession();
  const [error, setError] = useState('');
  return <>
    {error && <div className="ch-photo-error" role="alert">{error}<button type="button" aria-label="Dismiss photo error" onClick={() => setError('')}>×</button></div>}
    <CopilotChat threadId={threadId} labels={labels} chatView={PhotoChatView} attachments={{ enabled: true, accept: 'image/jpeg,image/png', maxSize: 20 * 1024 * 1024,
      onUpload: async file => { setError(''); return photoForChat(file, brand.id); }, onUploadFailed: e => setError(e.message) }} />
  </>;
}
